import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { formatPhoneNumber } from '@/utils/phoneFormat';
import { formatCurrency } from '@/utils/currencyFormat';
import { z } from 'zod';

const dischargeLoanSchema = z.object({
  formType: z.string().default('Discharge Mortgages, Car Loans & Credit Cards'),
  firstName: z.string().trim().min(1, { message: "First name is required" }).max(50, { message: "First name must be less than 50 characters" }),
  lastName: z.string().trim().min(1, { message: "Last name is required" }).max(50, { message: "Last name must be less than 50 characters" }),
  email: z.string().trim().email({ message: "Invalid email address" }).max(255, { message: "Email must be less than 255 characters" }),
  phone: z.string().trim().min(1, { message: "Phone number is required" }).max(20, { message: "Phone number must be less than 20 characters" }),
  loanType: z.enum(['car', 'mortgage', 'credit-card'], { required_error: "Please select loan type" }),
  balance: z.string().trim().min(1, { message: "Balance amount is required" }).max(20, { message: "Balance must be less than 20 characters" }),
  lender: z.string().trim().min(1, { message: "Lender name is required" }).max(100, { message: "Lender name must be less than 100 characters" })
});

type DischargeLoanFormData = z.infer<typeof dischargeLoanSchema>;

interface DischargeLoanFormProps {
  onSuccess?: () => void;
}

export function DischargeLoanForm({ onSuccess }: DischargeLoanFormProps = {}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<DischargeLoanFormData>({
    formType: 'Discharge Mortgages, Car Loans & Credit Cards',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    loanType: 'mortgage',
    balance: '',
    lender: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof DischargeLoanFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof DischargeLoanFormData, value: string) => {
    // Format phone number as user types
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }
    // Format currency fields as user types
    if (field === 'balance') {
      value = formatCurrency(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Close any open dropdowns
    document.body.click();

    try {
      const validatedData = dischargeLoanSchema.parse(formData);
      
      // Send data to Zapier webhook
      const response = await fetch('https://hooks.zapier.com/hooks/catch/21776725/u1pdpuq/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          ...validatedData,
          timestamp: new Date().toISOString(),
          source: window.location.origin
        }),
      });

      // Close the form first if onSuccess callback is provided
      if (onSuccess) {
        onSuccess();
      }

      // Small delay to let the form close before navigation
      setTimeout(() => {
        // Navigate to donation page after successful submission
        navigate('/discharge-loan-payment');
      }, 300);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof DischargeLoanFormData, string>> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            fieldErrors[err.path[0] as keyof DischargeLoanFormData] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Error",
          description: "Failed to submit your request. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      formType: 'Discharge Mortgages, Car Loans & Credit Cards',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      loanType: 'mortgage',
      balance: '',
      lender: ''
    });
    setErrors({});
  };

  return (
    <div className="max-w-2xl -mx-2 md:mx-auto">
      <Card className="bg-gradient-to-r from-primary/5 to-gold/5 border-gold/20">
        <CardHeader>
          <CardTitle className="font-rockwell text-2xl text-primary text-center">
            Get Started with Discharge Process
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="loanType">Loan Type</Label>
              <Select value={formData.loanType} onValueChange={(value) => handleInputChange('loanType', value)}>
                <SelectTrigger className={`focus:ring-gold focus:border-gold ${errors.loanType ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select loan type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mortgage">Mortgage</SelectItem>
                  <SelectItem value="car">Car Loan</SelectItem>
                  <SelectItem value="credit-card">Credit Card</SelectItem>
                </SelectContent>
              </Select>
              {errors.loanType && <p className="text-sm text-red-500">{errors.loanType}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="balance">Current Balance</Label>
              <Input
                id="balance"
                placeholder="e.g., $150,000"
                value={formData.balance}
                onChange={(e) => handleInputChange('balance', e.target.value)}
                className={errors.balance ? 'border-red-500' : ''}
              />
              {errors.balance && <p className="text-sm text-red-500">{errors.balance}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lender">Lender Name</Label>
              <Input
                id="lender"
                placeholder="e.g., Wells Fargo, Chase, etc."
                value={formData.lender}
                onChange={(e) => handleInputChange('lender', e.target.value)}
                className={errors.lender ? 'border-red-500' : ''}
              />
              {errors.lender && <p className="text-sm text-red-500">{errors.lender}</p>}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                className="flex-1"
              >
                Reset
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Processing...' : 'Next'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}